// src/app/core/http/retry.interceptor.ts
import type {
  HttpInterceptorFn, HttpRequest, HttpHandlerFn,
  HttpErrorResponse
} from '@angular/common/http';
import { inject } from '@angular/core';
import type { Observable} from 'rxjs';
import { throwError, timer } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { ConfigService } from '../config/config.service';

/**
 * Códigos HTTP que NO deben reintentarse — son errores definitivos del servidor.
 * El retry solo tiene sentido para errores de red o errores 5xx transitorios.
 */
const NO_RETRY_STATUSES = new Set([400, 401, 403, 404, 408, 409, 422, 429]);

/**
 * RetryInterceptor — reintenta peticiones GET fallidas con backoff exponencial.
 *
 * REGLAS:
 * - Solo reintenta GET — POST, PUT, DELETE no son idempotentes
 * - Máximo api.retryAttemptsOnGet intentos (del ConfigService)
 * - Backoff exponencial: 1s, 2s, 4s entre intentos
 * - No reintenta errores de autenticación, autorización o recursos no encontrados
 * - No reintenta 429 (rate limiting) — el servidor pide que no lo hagamos
 * - No reintenta 408 (timeout) — ya fue manejado por TimeoutInterceptor
 */
export const retryInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
) => {
  // Solo reintentar GETs
  if (req.method !== 'GET') {
    return next(req);
  }

  const config = inject(ConfigService);
let maxAttempts = 2; // fallback mientras el config no esté disponible
try {
  maxAttempts = config.get('api').retryAttemptsOnGet;
} catch {
  // Config aún no cargado (petición del APP_INITIALIZER) — usar fallback
}
  
  return attemptRequest(req, next, 0, maxAttempts);
};

function attemptRequest(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  attempt: number,
  maxAttempts: number
): Observable<ReturnType<typeof next> extends Observable<infer T> ? T : never> {
  return (next(req) as Observable<never>).pipe(
    catchError((error: HttpErrorResponse) => {
      const isLastAttempt    = attempt >= maxAttempts - 1;
      const isDefiniteError  = NO_RETRY_STATUSES.has(error.status);
      // status 0 = error de red (sin conexión, CORS, etc.) — SÍ reintentar
      const isNetworkError   = error.status === 0;

      if (isLastAttempt || (isDefiniteError && !isNetworkError)) {
        return throwError(() => error);
      }

      // Backoff exponencial: 2^attempt * 1000ms → 1000ms, 2000ms, 4000ms
      const delayMs = Math.pow(2, attempt) * 1000;

      return timer(delayMs).pipe(
        switchMap(() => attemptRequest(req, next, attempt + 1, maxAttempts) as Observable<never>)
      );
    })
  ) as ReturnType<typeof next> extends Observable<infer T> ? Observable<T> : never;
}
